// MarketingResourceView event payload definitions
import { createMarketingResourceView } from '@spotify-internal/event-definitions/es5/events/createMarketingResourceView';
import { createMarketingResourceViewNonAuth } from '@spotify-internal/event-definitions/es5/events/createMarketingResourceViewNonAuth';

// MarketingResourceLinkClick event payload definitions
import { createMarketingResourceLinkClick } from '@spotify-internal/event-definitions/es5/events/createMarketingResourceLinkClick';
import { createMarketingResourceLinkClickNonAuth } from '@spotify-internal/event-definitions/es5/events/createMarketingResourceLinkClickNonAuth';

// MarketingResourceView event payload definitions
import { createMarketingResourceComponentView } from '@spotify-internal/event-definitions/es5/events/createMarketingResourceComponentView';
import { createMarketingResourceComponentViewNonAuth } from '@spotify-internal/event-definitions/es5/events/createMarketingResourceComponentViewNonAuth';

// Benefits Comparison Table tooltip hover/focus payload definitions
import { createPremiumBenefitsComparisonTooltipHover } from '@spotify-internal/event-definitions/es5/events/createPremiumBenefitsComparisonTooltipHover';
import { createPremiumBenefitsComparisonTooltipHoverNonAuth } from '@spotify-internal/event-definitions/es5/events/createPremiumBenefitsComparisonTooltipHoverNonAuth';

// Storefront event payload definitions
import { createPremiumStorefrontView } from '@spotify-internal/event-definitions/es5/events/createPremiumStorefrontView';
import { createPremiumStorefrontViewNonAuth } from '@spotify-internal/event-definitions/es5/events/createPremiumStorefrontViewNonAuth';

// FAQ Component event payload definitions
import { createPremiumFAQComponentView } from '@spotify-internal/event-definitions/es5/events/createPremiumFAQComponentView';
import { createPremiumFAQComponentViewNonAuth } from '@spotify-internal/event-definitions/es5/events/createPremiumFAQComponentViewNonAuth';
import { createPremiumFAQComponentExpansionInteraction } from '@spotify-internal/event-definitions/es5/events/createPremiumFAQComponentExpansionInteraction';
import { createPremiumFAQComponentExpansionInteractionNonAuth } from '@spotify-internal/event-definitions/es5/events/createPremiumFAQComponentExpansionInteractionNonAuth';

import { GabitoEventDefinition, EventDefinition, Tracker } from './tracking';

export type GabitoEventDefinitionsWithAuth =
  GabitoEventDefinition['options'] & { authorize: boolean };

export type PageLoadParams = {
  resource_name?: string | null;
  country: string;
  locale: string | null;
  full_url: string;
  ref?: string | null;
  correlation_id: string | null;
  is_evergreen: boolean;
  eligibility: string | null;
  http_referer?: string | null;
  campaign_id?: string | null;
  offer_type_id?: string | null;
}; // timestamp should be auto-populated because of Browser Context.

export const pageLoadEvent = (
  eventData: PageLoadParams,
  options: GabitoEventDefinitionsWithAuth,
): EventDefinition[] => [
  {
    tracker: Tracker.Gabito,
    eventData: options.authorize
      ? createMarketingResourceView({
          resource_name: eventData.resource_name,
          country_code: eventData.country,
          locale: eventData.locale,
          full_url: eventData.full_url,
          ref: eventData.ref,
          correlation_id: eventData.correlation_id,
          is_evergreen: eventData.is_evergreen,
          eligibility: eventData.eligibility,
          http_referer: eventData.http_referer,
          campaign_id: eventData.campaign_id,
          offer_type_id: eventData.offer_type_id,
        })
      : createMarketingResourceViewNonAuth({
          resource_name: eventData.resource_name,
          country_code: eventData.country,
          locale: eventData.locale,
          full_url: eventData.full_url,
          ref: eventData.ref,
          correlation_id: eventData.correlation_id,
          is_evergreen: eventData.is_evergreen,
          eligibility: eventData.eligibility,
          http_referer: eventData.http_referer,
          campaign_id: eventData.campaign_id,
          offer_type_id: eventData.offer_type_id,
        }),
    options,
  },
];

export type LinkClickParams = {
  resource_name?: string | null;
  link_url: string | null;
  country: string;
  locale: string | null;
  offer_type_id?: string | null;
  timestamp?: number | null;
  full_url: string;
  correlation_id: string | null;
  is_evergreen: boolean;
  eligibility: string | null;
  component_view: string | null;
}; // correlation_id and timestamp should be auto-populated because of Browser Context.

export const linkClickEvent = (
  eventData: LinkClickParams,
  options: GabitoEventDefinitionsWithAuth,
): EventDefinition[] => [
  {
    tracker: Tracker.Gabito,
    eventData: options.authorize
      ? createMarketingResourceLinkClick({
          resource_name: eventData.resource_name,
          link_url: eventData.link_url,
          country_code: eventData.country,
          offer_type_id: eventData?.offer_type_id,
          timestamp: eventData?.timestamp,
          locale: eventData.locale,
          full_url: eventData.full_url,
          correlation_id: eventData.correlation_id,
          is_evergreen: eventData.is_evergreen,
          eligibility: eventData.eligibility,
          component_view: eventData.component_view,
        })
      : createMarketingResourceLinkClickNonAuth({
          resource_name: eventData.resource_name,
          link_url: eventData.link_url,
          country_code: eventData.country,
          offer_type_id: eventData?.offer_type_id,
          timestamp: eventData?.timestamp,
          locale: eventData.locale,
          full_url: eventData.full_url,
          correlation_id: eventData.correlation_id,
          is_evergreen: eventData.is_evergreen,
          eligibility: eventData.eligibility,
          component_view: eventData.component_view,
        }),
    options,
  },
];

export type componentViewParams = {
  resource_name?: string | null;
  component_name: string | null;
  country: string;
  locale: string | null;
  full_url: string;
  ref?: string | null;
  correlation_id: string | null;
  is_evergreen: boolean;
  eligibility: string | null;
  offer_type_id?: string | null;
}; // correlation_id and timestamp should be auto-populated because of Browser Context.

export const componentViewEvent = (
  eventData: componentViewParams,
  options: GabitoEventDefinitionsWithAuth,
): EventDefinition[] => [
  {
    tracker: Tracker.Gabito,
    eventData: options.authorize
      ? createMarketingResourceComponentView({
          resource_name: eventData.resource_name,
          component_name: eventData.component_name,
          country_code: eventData.country,
          locale: eventData.locale,
          full_url: eventData.full_url,
          correlation_id: eventData.correlation_id,
          is_evergreen: eventData.is_evergreen,
          eligibility: eventData.eligibility,
          offer_type_id: eventData.offer_type_id,
        })
      : createMarketingResourceComponentViewNonAuth({
          resource_name: eventData.resource_name,
          component_name: eventData.component_name,
          country_code: eventData.country,
          locale: eventData.locale,
          full_url: eventData.full_url,
          correlation_id: eventData.correlation_id,
          is_evergreen: eventData.is_evergreen,
          eligibility: eventData.eligibility,
          offer_type_id: eventData.offer_type_id,
        }),
    options,
  },
];

export type PremiumStorefrontInViewParams = {
  country: string;
  locale: string | null;
  full_url: string;
  timestamp: number;
  sorting_order: Array<string> | null;
  correlation_id: string | undefined;
  is_evergreen: boolean;
  eligibility: string | null;
};

export const premiumStorefrontInViewEvent = (
  eventData: PremiumStorefrontInViewParams,
  options: GabitoEventDefinitionsWithAuth,
): EventDefinition[] => [
  {
    tracker: Tracker.Gabito,
    eventData: options.authorize
      ? createPremiumStorefrontView({
          country: eventData.country,
          locale: eventData.locale,
          full_url: eventData.full_url,
          timestamp: eventData.timestamp,
          sorting_order: eventData.sorting_order,
          correlation_id: eventData.correlation_id,
          is_evergreen: eventData.is_evergreen,
          eligibility: eventData.eligibility,
        })
      : createPremiumStorefrontViewNonAuth({
          country: eventData.country,
          locale: eventData.locale,
          full_url: eventData.full_url,
          timestamp: eventData.timestamp,
          sorting_order: eventData.sorting_order,
          correlation_id: eventData.correlation_id,
          is_evergreen: eventData.is_evergreen,
          eligibility: eventData.eligibility,
        }),
    options,
  },
];

export type PremiumStorefrontInteractionParams = {
  country: string;
  locale: string;
  full_url: string;
  timestamp: number;
  action_name: string;
  button_url: string;
  offer_type_id: string;
  correlation_id: string | null;
  is_evergreen: boolean;
  eligibility: string | null;
};

export type PremiumFAQInViewParams = {
  country: string;
  locale: string | null;
  full_url: string;
  timestamp: number;
  correlation_id: string | undefined;
  is_evergreen: boolean;
  eligibility: string | null;
};

export const premiumFAQInViewEvent = (
  eventData: PremiumFAQInViewParams,
  options: GabitoEventDefinitionsWithAuth,
): EventDefinition[] => [
  {
    tracker: Tracker.Gabito,
    eventData: options.authorize
      ? createPremiumFAQComponentView({
          country: eventData.country,
          locale: eventData.locale,
          full_url: eventData.full_url,
          timestamp: eventData.timestamp,
          correlation_id: eventData.correlation_id,
          is_evergreen: eventData.is_evergreen,
          eligibility: eventData.eligibility,
        })
      : createPremiumFAQComponentViewNonAuth({
          country: eventData.country,
          locale: eventData.locale,
          full_url: eventData.full_url,
          timestamp: eventData.timestamp,
          correlation_id: eventData.correlation_id,
          is_evergreen: eventData.is_evergreen,
          eligibility: eventData.eligibility,
        }),
    options,
  },
];

export type premiumFAQExpansionParams = {
  country: string;
  locale: string;
  full_url: string;
  timestamp: number;
  faq_interaction: string;
  correlation_id: string | null;
  is_evergreen: boolean;
  eligibility: string | null;
};

export const premiumFAQExpansionEvent = (
  eventData: premiumFAQExpansionParams,
  options: GabitoEventDefinitionsWithAuth,
): EventDefinition[] => [
  {
    tracker: Tracker.Gabito,
    eventData: options.authorize
      ? createPremiumFAQComponentExpansionInteraction({
          country: eventData.country,
          locale: eventData.locale,
          full_url: eventData.full_url,
          timestamp: eventData.timestamp,
          faq_interaction: eventData.faq_interaction,
          correlation_id: eventData.correlation_id,
          is_evergreen: eventData.is_evergreen,
          eligibility: eventData.eligibility,
        })
      : createPremiumFAQComponentExpansionInteractionNonAuth({
          country: eventData.country,
          locale: eventData.locale,
          full_url: eventData.full_url,
          timestamp: eventData.timestamp,
          faq_interaction: eventData.faq_interaction,
          correlation_id: eventData.correlation_id,
          is_evergreen: eventData.is_evergreen,
          eligibility: eventData.eligibility,
        }),
    options,
  },
];

export type PremiumBenefitsComparisonTooltipHoverParams = {
  country: string;
  locale: string;
  full_url: string;
  timestamp: number;
  correlation_id: string | null;
  tooltip_interaction: string | null;
  is_evergreen: boolean;
  eligibility: string | null;
};

export const premiumBenefitsComparisonTooltipHoverEvent = (
  eventData: PremiumBenefitsComparisonTooltipHoverParams,
  options: GabitoEventDefinitionsWithAuth,
): EventDefinition[] => [
  {
    tracker: Tracker.Gabito,
    eventData: options.authorize
      ? createPremiumBenefitsComparisonTooltipHover({
          country: eventData.country,
          locale: eventData.locale,
          full_url: eventData.full_url,
          timestamp: eventData.timestamp,
          tooltip_interaction: eventData.tooltip_interaction,
          correlation_id: eventData.correlation_id,
          is_evergreen: eventData.is_evergreen,
          eligibility: eventData.eligibility,
        })
      : createPremiumBenefitsComparisonTooltipHoverNonAuth({
          country: eventData.country,
          locale: eventData.locale,
          full_url: eventData.full_url,
          timestamp: eventData.timestamp,
          tooltip_interaction: eventData.tooltip_interaction,
          correlation_id: eventData.correlation_id,
          is_evergreen: eventData.is_evergreen,
          eligibility: eventData.eligibility,
        }),
    options,
  },
];
